import { ChangeEvent, KeyboardEvent, RefObject, SyntheticEvent, useEffect, useRef, useState } from "react"
import { Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Tab, Table, Tabs } from "react-bootstrap"
import { SelectInstance } from "react-select"
import AsyncSelect from "react-select/async"
import { SelectOptionType } from "../../constants/SelectOptionType"
import ContentBody from "../../components/ContentBody"
import ContentHeader from "../../components/ContentHeader"
import DeleteConfirmation from "../../components/DeleteConfirmation"
import Pagination from "../../components/Pagination"
import Configs from "../../Configs"
import ProviderService, { IProvider } from "../../services/ProviderService"
import UserService, { IUser } from "../../services/UserService"
import useAuthContext from "../../hooks/useAuthContext"
import Permissions from "../../constants/Permissions"
import { UserTypes } from "../../constants/UserTypes"

function Providers() {
    const { hasPermission } = useAuthContext()

    const [tableData, setTableData] = useState({
        data: new Array<IProvider>(),
        hasNext: false,
        hasPrevious: false,
        pageIndex: 1,
        pageSize: Configs.DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0
    })

    const initFormData = {
        id: null,
        name: "",
        address: "",
        email: "",
        description: "",
        about: "",
        logo: "",
        banner: "",
        users: new Array<IUser>()
    } as IProvider

    const [logoFile, setLogoFile] = useState(null as any)
    const [bannerFile, setBannerFile] = useState(null as any)
    const [formData, setFormData] = useState(initFormData)
    const [message, setMessage] = useState("")
    const [messageFormModal, setMessageFormModal] = useState("")
    const [validatedFormModal, setValidatedFormModal] = useState(false)
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [deletedItem, setDeletedItem] = useState({
        id: 0,
        name: ""
    })

    const keywordRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)

    const FilterFn = async (pageIndex: number) => {
        let pageSize = Configs.DEFAULT_PAGE_SIZE
        let keyword = keywordRef.current?.value || ""

        const res = await ProviderService.filter(pageIndex, pageSize, keyword)

        if (res?.isSuccess) {
            setTableData(res.data)
        } else {
            console.log(res?.message)
        }

        setCurrentPageIndex(pageIndex)
    }

    useEffect(() => {
        keywordRef.current?.focus()

        FilterFn(1)
    }, [])

    const handleSearch = () => {
        FilterFn(1)
    }

    const handleSearchInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            FilterFn(1)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        FilterFn(pageNumber)
    }

    const handleFormControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const handleLogoFileControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            setLogoFile(e.target.files[0])
        } else {
            setLogoFile(null)
        }
    }

    const handleBannerFileControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            setBannerFile(e.target.files[0])
        } else {
            setBannerFile(null)
        }
    }

    const handleIsUsedSwitch = async (id: number | null) => {
        if (id) {
            const res = await ProviderService.changeIsUsed(id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            } else {
                console.log(res?.message)
            }
        }
    }

    const handleAddNew = () => {
        setFormData(initFormData)
        handleShowModal()
    }

    //Delete
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false)

    const hideConfirmationModal = () => {
        setDeletedItem({
            id: 0,
            name: ""
        })

        setDisplayConfirmationModal(false)
    }

    const showDeleteConfirmation = (id: number | null, name: string) => {
        if (id) {
            setDeletedItem({
                id: id,
                name: name
            })

            setDisplayConfirmationModal(true)
            setMessage("")
        }
    }

    const handleDelete = async () => {
        const res = await ProviderService.delete(deletedItem.id)

        if (res?.isSuccess) {
            FilterFn(currentPageIndex)
        }

        hideConfirmationModal()
        setMessage(res.message)
    }

    //Modal
    const [showModal, setShowModal] = useState(false)
    const handleCloseModal = () => {
        setShowModal(false)
        setValidatedFormModal(false)
        setMessageFormModal("")
    }
    const handleShowModal = () => {
        setLogoFile(null)
        setBannerFile(null)
        setMessage("")
        setShowModal(true)
    }

    const modalTitle = formData.id !== null ? "Edit Provider" : "New Provider"

    const handleEdit = async (id: number | null) => {
        if (id) {
            const res = await ProviderService.get(id)

            if (res?.isSuccess) {
                setFormData({
                    ...initFormData,
                    id: res.data.id,
                    name: res.data.name,
                    address: res.data.address,
                    email: res.data.email,
                    description: res.data.description,
                    about: res.data.about,
                    logo: res.data.logo,
                    banner: res.data.banner,
                    users: res.data.users
                })

                handleShowModal()
            } else {
                console.log(res?.message)
            }
        }
    }

    const handleSave = async (e: SyntheticEvent) => {
        e.preventDefault()

        var frmData = new FormData()
        if (logoFile !== null) {
            frmData.append("Logo", logoFile)
        }

        if (bannerFile !== null) {
            frmData.append("Banner", bannerFile)
        }

        frmData.append("Name", formData.name || "")
        frmData.append("Address", formData.address || "")
        frmData.append("Email", formData.email || "")
        frmData.append("Description", formData.description || "")
        frmData.append("About", formData.about || "")

        formData.users.forEach((user, index) => {
            frmData.append(`Users[${index}].Id`, user.id.toString())
        })

        if (formRef.current?.checkValidity()) {
            if (formData.id) {
                const res = await ProviderService.update(formData.id, frmData)

                if (res?.isSuccess) {
                    FilterFn(currentPageIndex)
                    handleCloseModal()
                    setMessage(res.message)
                } else {
                    setMessageFormModal(res?.message)
                }
            } else {
                const res = await ProviderService.create(frmData)

                if (res?.isSuccess) {
                    FilterFn(1)
                    handleCloseModal()
                    setMessage(res.message)
                } else {
                    setMessageFormModal(res?.message)
                }
            }
        } else {
            setValidatedFormModal(true)
        }
    }

    //AsyncSelect
    const [selectedOption, setSelectedOption] = useState(null as SelectOptionType | null)
    const asyncRef = useRef<SelectInstance<SelectOptionType> | null>(null)

    const handleSelectChange = async (option: SelectOptionType | null) => {
        setSelectedOption(option)

        if (option?.value) {
            let value = option?.value as number

            if (formData.users.filter(x => x.id === value).length === 0) {
                var res = await UserService.get(value)

                if (res.isSuccess) {
                    formData.users.push(res.data)

                    setFormData(formData)

                    setSelectedOption(null)
                    asyncRef.current?.focus()
                }
            }
        }
    }

    const filterUsers = async (inputValue: string) => {
        var options: SelectOptionType[] = []

        var res = await UserService.search(UserTypes.PROVIDER, inputValue)

        if (res.isSuccess && res.data) {
            options = res.data.map((item: IUser) => {
                return { value: item.id, label: item.userName }
            })
        }

        return options
    }

    const loadUserOptions = (inputValue: string) => new Promise<SelectOptionType[]>((resolve) => {
        resolve(filterUsers(inputValue))
    })

    const handleRemoveUser = (userId: number | null) => {
        setFormData({ ...formData, users: formData.users.filter(x => x.id !== userId) })
    }

    return (
        <>
            <ContentHeader title="Providers" />

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    {hasPermission(Permissions.Providers.Create) &&
                                        <Button variant="info" size="sm" onClick={handleAddNew}>
                                            <i className="fas fa-plus"></i> Add New
                                        </Button>
                                    }

                                    <div className="card-tools">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search"
                                                ref={keywordRef as RefObject<HTMLInputElement>}
                                                onKeyUp={handleSearchInputKeyPress}
                                            />
                                            <Button variant="info" size="sm" onClick={handleSearch}>
                                                <i className="fas fa-search"></i>
                                            </Button>
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                                    <Table responsive striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: "center", width: "80px" }}>Logo</th>
                                                <th>Name</th>
                                                <th>Address</th>
                                                <th>Email</th>
                                                <th style={{ textAlign: "center", width: "100px" }}>Is Used</th>
                                                <th style={{ textAlign: "center", width: "90px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                tableData.data.map(item =>
                                                    <tr key={item.id}>
                                                        <td>{item.logo && <img src={`${Configs.API_BASE_URL}${item.logo}`} style={{ maxWidth: "100%" }} alt={item.name} />}</td>
                                                        <td>{item.name}</td>
                                                        <td>{item.address}</td>
                                                        <td>{item.email}</td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <Form.Check type="switch" id={"provider_isused_" + item.id} checked={item.isUsed} onChange={() => handleIsUsedSwitch(item.id)} disabled={!hasPermission(Permissions.Providers.Edit)} />
                                                        </td>
                                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                            {hasPermission(Permissions.Providers.Edit) &&
                                                                <Button variant="warning" size="sm" className="mr-1" onClick={() => handleEdit(item.id)}><i className="fas fa-pencil-alt"></i> Edit</Button>
                                                            }
                                                            {hasPermission(Permissions.Providers.Delete) &&
                                                                <Button variant="danger" size="sm" onClick={() => showDeleteConfirmation(item.id, item.name)}><i className="fas fa-trash-alt"></i> Delete</Button>
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer>
                                    <Pagination hasNext={tableData.hasNext} hasPrevious={tableData.hasPrevious} pageIndex={tableData.pageIndex} pageSize={tableData.pageSize} totalPages={tableData.totalPages} totalRecords={tableData.totalRecords} handlePageChange={handlePageChange} />
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>

            <DeleteConfirmation show={displayConfirmationModal} onConfirm={handleDelete} onHide={hideConfirmationModal} id={deletedItem.id} name={deletedItem.name} />

            <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validatedFormModal} ref={formRef as RefObject<HTMLFormElement>}>
                        <Tabs defaultActiveKey="logo">
                            <Tab eventKey="logo" title="Logo">
                                <Form.Group controlId="logoFile" className="mb-3">
                                    <Card className="m-0 border-0 shadow-0">
                                        {formData.logo && <Card.Img variant="top" src={`${Configs.API_BASE_URL}${formData.logo}`} style={{ maxWidth: "200px", margin: "auto" }} />}
                                        <Card.Body className="p-2">
                                            <Form.Control type="file" name="logo" onChange={handleLogoFileControlChange} accept={Configs.IMAGE_EXTENSION} />
                                        </Card.Body>
                                    </Card>
                                </Form.Group>
                            </Tab>
                            <Tab eventKey="banner" title="Banner">
                                <Form.Group controlId="bannerFile" className="mb-3">
                                    <Card className="m-0 border-0 shadow-0">
                                        {formData.banner && <Card.Img variant="top" src={`${Configs.API_BASE_URL}${formData.banner}`} style={{ maxWidth: "500px", margin: "auto" }} />}
                                        <Card.Body className="p-2">
                                            <Form.Control type="file" name="banner" onChange={handleBannerFileControlChange} accept={Configs.IMAGE_EXTENSION} />
                                        </Card.Body>
                                    </Card>
                                </Form.Group>
                            </Tab>
                        </Tabs>
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control type="text" name="name" required value={formData.name || ""} onChange={(e) => handleFormControlChange(e as any)} />
                            <Form.Control.Feedback type="invalid">Name is required.</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="address">
                            <Form.Label>Address</Form.Label>
                            <Form.Control type="text" name="address" value={formData.address || ""} onChange={(e) => handleFormControlChange(e as any)} />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control type="email" name="email" value={formData.email || ""} onChange={(e) => handleFormControlChange(e as any)} />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="description">
                            <Form.Label>Description</Form.Label>
                            <Form.Control type="text" name="description" value={formData.description || ""} onChange={(e) => handleFormControlChange(e as any)} />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="about">
                            <Form.Label>About</Form.Label>
                            <Form.Control as="textarea" rows={7} name="about" value={formData.about || ""} onChange={(e) => handleFormControlChange(e as any)} />
                        </Form.Group>
                        <fieldset>
                            <legend>Provider Users</legend>
                            <AsyncSelect className="mb-3" isClearable value={selectedOption} loadOptions={loadUserOptions} onChange={handleSelectChange} ref={asyncRef} placeholder="Nhập thông tin người dùng để tìm kiếm." noOptionsMessage={({ inputValue }) => !inputValue ? "Nhập thông tin người dùng để tìm kiếm." : "Không tìm thấy người dùng nào!"} />
                            <Table responsive hover bordered size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Username</th>
                                        <th>Email</th>
                                        <th style={{ width: "40px" }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.users.map((user: IUser) =>
                                        <tr key={user.id}>
                                            <td>
                                                {user.userName || ""}
                                            </td>
                                            <td>
                                                {user.email || ""}
                                            </td>
                                            <td>
                                                <Button variant="light" size="sm" onClick={() => handleRemoveUser(user.id)}><i className="fas fa-times"></i></Button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </fieldset>
                    </Form>
                    {messageFormModal !== "" && <Alert variant="danger" onClose={() => setMessageFormModal("")} dismissible>{messageFormModal}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleCloseModal}><i className="fas fa-times"></i> Cancel</Button>
                    <Button variant="info" onClick={handleSave}><i className="fas fa-save"></i> Save</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default Providers